<template>
  <div class="join-us-page">
    <!-- 横幅部分 -->
    <section class="hero-banner bg-orange-500 py-16 lg:py-20">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          <!-- 左侧内容 -->
          <div class="flex-1 text-center lg:text-left">
            <h1 class="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              {{ banner.title }}
            </h1>
            <p class="text-xl text-white mb-8 opacity-90">
              {{ banner.subtitle }}
            </p>
            <a href="#openings" class="inline-block px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              {{ banner.button_text }}
            </a>
          </div>

          <!-- 右侧图标 -->
          <div class="flex-shrink-0 w-64 h-64 lg:w-80 lg:h-80">
            <img :src="banner.image_url || '/assets/23@1x.png'" alt="加入我们" class="w-full h-full object-contain">
          </div>
        </div>
      </div>
    </section>



    <!-- 招聘职位 -->
    <section id="openings" class="openings py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {{ openings.title }}
          </h2>
          <p class="text-lg text-gray-600">{{ openings.subtitle }}</p>
        </div>

        <!-- 职位分类 -->
        <div class="mb-8">
          <div class="flex flex-wrap gap-3">
            <button
              @click="filterDepartment('all')"
              :class="['px-6 py-2 rounded-full font-medium transition-colors', selectedDepartment === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            >
              {{ filterLabels.all }}
            </button>
            <button
              v-for="dept in departments"
              :key="dept.key"
              @click="filterDepartment(dept.key)"
              :class="['px-6 py-2 rounded-full font-medium transition-colors', selectedDepartment === dept.key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            >
              {{ dept.label }}
            </button>
          </div>
        </div>

        <!-- 职位列表 -->
        <div class="space-y-4">
          <div
            v-for="job in filteredJobs"
            :key="job.id"
            class="job-card border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all"
          >
            <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div class="flex-1">
                <div class="flex items-start gap-4 mb-3">
                  <div>
                    <h3 class="text-xl font-bold text-gray-900 mb-2">{{ job.title }}</h3>
                    <div class="flex flex-wrap gap-2 mb-2">
                      <span class="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        {{ getDepartmentLabel(job.department) }}
                      </span>
                      <span class="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        {{ job.location }}
                      </span>
                      <span class="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                        {{ job.employment_type }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-html="job.description" class="text-gray-600 text-sm mb-3 prose prose-sm max-w-none"></div>
                <div class="flex flex-wrap gap-4 text-xs text-gray-500">
                  <span v-if="job.salary">{{ $t('joinUs.salary') }}: {{ job.salary }}</span>
                  <span v-if="job.experience">{{ $t('joinUs.experience') }}: {{ job.experience }}</span>
                  <span>{{ $t('joinUs.posted') }}: {{ formatDate(job.posted_date) }}</span>
                </div>
              </div>
              <div class="flex-shrink-0">
                <button
                  @click="viewJobDetail(job)"
                  class="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  {{ $t('joinUs.apply') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="filteredJobs.length === 0" class="text-center py-16">
          <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p class="text-gray-500 text-lg">{{ $t('joinUs.noOpenings') }}</p>
        </div>
      </div>
    </section>



    <!-- 职位详情弹窗 -->
    <div v-if="selectedJob" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="closeJobDetail">
      <div class="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6 lg:p-8">
          <!-- 头部 -->
          <div class="flex items-start justify-between mb-6">
            <div class="flex-1">
              <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                {{ selectedJob.title }}
              </h2>
              <div class="flex flex-wrap gap-2 mb-3">
                <span class="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {{ getDepartmentLabel(selectedJob.department) }}
                </span>
                <span class="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                  {{ selectedJob.location }}
                </span>
                <span class="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                  {{ selectedJob.employment_type }}
                </span>
              </div>
              <div class="flex flex-wrap gap-4 text-sm text-gray-500">
                <span v-if="selectedJob.salary">{{ $t('joinUs.salary') }}: {{ selectedJob.salary }}</span>
                <span v-if="selectedJob.experience">{{ $t('joinUs.experience') }}: {{ selectedJob.experience }}</span>
              </div>
            </div>
            <button @click="closeJobDetail" class="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- 职位描述 -->
          <div class="space-y-6">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('joinUs.description') }}</h3>
              <div v-html="selectedJob.description" class="prose max-w-none text-gray-700"></div>
            </div>

            <div v-if="selectedJob.responsibilities">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('joinUs.responsibilities') }}</h3>
              <div v-html="selectedJob.responsibilities" class="prose max-w-none text-gray-700"></div>
            </div>

            <div v-if="selectedJob.requirements">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('joinUs.requirements') }}</h3>
              <div v-html="selectedJob.requirements" class="prose max-w-none text-gray-700"></div>
            </div>

            <div v-if="selectedJob.benefits">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('joinUs.jobBenefits') }}</h3>
              <div v-html="selectedJob.benefits" class="prose max-w-none text-gray-700"></div>
            </div>
          </div>

          <!-- 申请按钮 -->
          <div class="mt-8 pt-6 border-t">
            <a
              :href="'mailto:' + contact.email + '?subject=应聘: ' + selectedJob.title"
              class="inline-block w-full md:w-auto px-8 py-3 bg-orange-500 text-white text-center rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              {{ $t('joinUs.applyNow') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- 富文本内容全局样式 -->
<style>
.prose h1 {
  font-size: 1.875rem;
  font-weight: 700;
  margin-top: 1.5em;
  margin-bottom: 0.75em;
  line-height: 1.2;
  color: #111827;
}

.prose h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.25em;
  margin-bottom: 0.75em;
  line-height: 1.3;
  color: #111827;
}

.prose h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1em;
  margin-bottom: 0.5em;
  line-height: 1.4;
  color: #111827;
}

.prose p {
  margin-top: 0.5em;
  margin-bottom: 1em;
  line-height: 1.75;
  color: #4b5563;
}

.prose ul {
  list-style-type: disc;
  padding-left: 1.5em;
  margin-top: 0.75em;
  margin-bottom: 1em;
}

.prose ol {
  list-style-type: decimal;
  padding-left: 1.5em;
  margin-top: 0.75em;
  margin-bottom: 1em;
}

.prose li {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  line-height: 1.75;
  color: #4b5563;
}

.prose li::marker {
  color: #f97316;
}

.prose strong {
  font-weight: 700;
  color: #111827;
}

.prose em {
  font-style: italic;
}

.prose a {
  color: #ea580c;
  text-decoration: underline;
  transition: color 0.2s;
}

.prose a:hover {
  color: #c2410c;
}

.prose ul ul,
.prose ol ul,
.prose ul ol,
.prose ol ol {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

/* 小号富文本样式（用于卡片） */
.prose-sm h1 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-top: 0.75em;
  margin-bottom: 0.5em;
  line-height: 1.2;
  color: #111827;
}

.prose-sm h2 {
  font-size: 1.125rem;
  font-weight: 600;
  margin-top: 0.75em;
  margin-bottom: 0.5em;
  line-height: 1.3;
  color: #111827;
}

.prose-sm h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-top: 0.5em;
  margin-bottom: 0.25em;
  line-height: 1.4;
  color: #111827;
}

.prose-sm p {
  font-size: 0.875rem;
  margin-top: 0.25em;
  margin-bottom: 0.5em;
  line-height: 1.5;
  color: #4b5563;
}

.prose-sm ul {
  font-size: 0.875rem;
  list-style-type: disc;
  padding-left: 1.25em;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.prose-sm ol {
  font-size: 0.875rem;
  list-style-type: decimal;
  padding-left: 1.25em;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.prose-sm li {
  font-size: 0.875rem;
  margin-top: 0.25em;
  margin-bottom: 0.25em;
  line-height: 1.5;
  color: #4b5563;
}

.prose-sm li::marker {
  color: #f97316;
}

.prose-sm strong {
  font-weight: 700;
  color: #111827;
}

.prose-sm em {
  font-style: italic;
}

.prose-sm a {
  color: #ea580c;
  text-decoration: underline;
  transition: color 0.2s;
}

.prose-sm a:hover {
  color: #c2410c;
}

.prose-sm ul ul,
.prose-sm ol ul,
.prose-sm ul ol,
.prose-sm ol ol {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}
</style>

<script>
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/api';

export default {
  name: 'JoinUsPage',
  data() {
    return {
      banner: {
        title: '加入我们',
        subtitle: '与红土证券一起成长，共创美好未来',
        button_text: '查看职位'
      },
      whyJoinUs: {
        title: '为什么选择红土证券',
        subtitle: '我们提供充满挑战和机遇的工作环境，让每位员工都能发挥最大潜力',
        reasons: [
          {
            title: '行业领先',
            description: '香港知名的金融科技券商'
          },
          {
            title: '成长空间',
            description: '完善的培训体系和晋升通道'
          },
          {
            title: '福利待遇',
            description: '具有竞争力的薪酬福利'
          },
          {
            title: '工作氛围',
            description: '开放、平等、协作的团队文化'
          }
        ]
      },
      benefits: {
        title: '员工福利',
        items: [
          { title: '五险一金', description: '按国家规定为员工缴纳社会保险和住房公积金' },
          { title: '带薪年假', description: '享受国家法定年假及公司额外年假' },
          { title: '节日福利', description: '重要节日发放节日礼品和礼金' },
          { title: '体检福利', description: '每年提供一次全面健康体检' },
          { title: '团队建设', description: '定期组织团队建设活动' },
          { title: '培训发展', description: '提供丰富的内外部培训机会' }
        ]
      },
      openings: {
        title: '热招职位',
        subtitle: '寻找有才华的你，加入我们的团队'
      },
      departments: [
        { key: 'tech', label: '技术研发', labelEn: 'Technology' },
        { key: 'product', label: '产品', labelEn: 'Product' },
        { key: 'operations', label: '运营', labelEn: 'Operations' },
        { key: 'marketing', label: '市场', labelEn: 'Marketing' },
        { key: 'sales', label: '销售', labelEn: 'Sales' }
      ],
      filterLabels: {
        all: '全部',
        allEn: 'All'
      },
      selectedDepartment: 'all',
      jobs: [],
      contact: {
        title: '联系我们',
        subtitle: '如有任何疑问，欢迎随时联系我们',
        email_title: '邮箱',
        email: 'hr@redlandsec.com',
        phone_title: '电话',
        phone: '+852 84830276',
        address_title: '地址',
        address: '香港特别行政区'
      },
      selectedJob: null
    };
  },
  computed: {
    currentLang() {
      return this.$i18n.locale || 'zh';
    },
    filteredJobs() {
      if (this.selectedDepartment === 'all') {
        return this.jobs;
      }
      return this.jobs.filter(job => job.department === this.selectedDepartment);
    }
  },
  async mounted() {
    await this.loadJobs();

    // 监听语言切换
    window.addEventListener('localeChange', this.handleLocaleChange);
  },
  beforeDestroy() {
    window.removeEventListener('localeChange', this.handleLocaleChange);
  },
  methods: {
    async loadJobs() {
      try {
        const params = new URLSearchParams({
          lang: this.currentLang,
          status: 'active'
        });

        const response = await fetch(`${API_BASE_URL}/public/jobs?${params}`);
        const result = await response.json();

        if (result.code === 0 && result.data) {
          const jobs = result.data.list || [];

          // 如果当前语言没有数据，且不是简体中文，则fallback到简体中文
          if (jobs.length === 0 && this.currentLang !== 'zh') {
            console.log(`${this.currentLang} language has no jobs data, falling back to zh`);
            const fallbackParams = new URLSearchParams({
              lang: 'zh',
              status: 'active'
            });
            const fallbackResponse = await fetch(`${API_BASE_URL}/public/jobs?${fallbackParams}`);
            const fallbackResult = await fallbackResponse.json();
            if (fallbackResult.code === 0 && fallbackResult.data) {
              this.jobs = fallbackResult.data.list || [];
            } else {
              this.jobs = [];
            }
          } else {
            this.jobs = jobs;
          }
        }
      } catch (error) {
        console.error('加载职位失败:', error);
      }
    },
    filterDepartment(department) {
      this.selectedDepartment = department;
    },
    viewJobDetail(job) {
      this.selectedJob = job;
    },
    closeJobDetail() {
      this.selectedJob = null;
    },
    getDepartmentLabel(department) {
      const dept = this.departments.find(d => d.key === department);
      return dept ? (this.currentLang === 'en' ? dept.labelEn : dept.label) : department;
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString(this.currentLang === 'en' ? 'en-US' : 'zh-CN');
    },
    handleLocaleChange() {
      this.loadJobs();
    }
  }
};
</script>
